import { inject, Injectable } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import {
  GetLKPDisciplinasRequest,
  GetLKPIntervaloRequest,
  GetLKPPeriodoRequest,
  GetLKPTurmaRequest,
  GetTitulosRequest,
  ListaLKPDisciplinas,
  ListaLKPIntervalo,
  ListaLKPPeriodo,
  ListaLKPTurma,
  ListaTituloPlanoAula,
} from '../models';
import { PlanoAulaLoadService } from './plano-aula-load.service';

/**
 * Cache em memória para lookups de plano de aula.
 * Reutiliza a mesma requisição in-flight (shareReplay) para os mesmos parâmetros.
 */
@Injectable({
  providedIn: 'root',
})
export class PlanoAulaCacheService {
  private readonly loadService = inject(PlanoAulaLoadService);

  private readonly titulos$ = new Map<string, Observable<ListaTituloPlanoAula>>();
  private readonly periodos$ = new Map<string, Observable<ListaLKPPeriodo>>();
  private readonly lkpDisciplina$ = new Map<
    string,
    Observable<ListaLKPDisciplinas>
  >();
  private readonly turmas$ = new Map<string, Observable<ListaLKPTurma>>();
  private readonly intervalos$ = new Map<string, Observable<ListaLKPIntervalo>>();

  getTitulos(request: GetTitulosRequest): Observable<ListaTituloPlanoAula> {
    return this.cached(
      this.titulos$,
      `${request.DATABASE}_${request.ETAPA_BNCC}`,
      () => this.loadService.getTitulos(request),
    );
  }

  getPeriodo(request: GetLKPPeriodoRequest): Observable<ListaLKPPeriodo> {
    return this.cached(
      this.periodos$,
      `${request.ID_TURMA}_${request.ANO}_${request.CDESCOLA}_${request.DATABASE}`,
      () => this.loadService.getPeriodo(request),
    );
  }

  getLKPDisciplinas(
    request: GetLKPDisciplinasRequest,
  ): Observable<ListaLKPDisciplinas> {
    return this.cached(
      this.lkpDisciplina$,
      `${request.ID_TURMA}_${request.BIMESTRE}_${request.ANO}_${request.CPF}`,
      () => this.loadService.getLKPDisciplinas(request),
    );
  }

  getLKPTurma(request: GetLKPTurmaRequest): Observable<ListaLKPTurma> {
    return this.cached(
      this.turmas$,
      [
        request.ANO,
        request.CDESCOLA,
        request.CPF,
        request.PERIODO,
        request.INCLUSAO,
        request.DATABASE,
        request.CPF_COORDENADOR ?? '',
        request.CDSERIE ?? '',
        request.EXERC ?? '',
      ].join('_'),
      () => this.loadService.getLKPTurma(request),
    );
  }

  getLKPIntervalo(
    request: GetLKPIntervaloRequest,
  ): Observable<ListaLKPIntervalo> {
    return this.cached(
      this.intervalos$,
      `${request.INTERVALOAVALIACAO}_${request.DATABASE}`,
      () => this.loadService.getLKPIntervalo(request),
    );
  }

  invalidateCache(): void {
    this.titulos$.clear();
    this.periodos$.clear();
    this.lkpDisciplina$.clear();
    this.turmas$.clear();
    this.intervalos$.clear();
  }

  invalidateTitulosCache(): void {
    this.titulos$.clear();
  }

  invalidatePeriodoCache(): void {
    this.periodos$.clear();
  }

  invalidateDisciplinaCache(): void {
    this.lkpDisciplina$.clear();
  }

  invalidateTurmaCache(): void {
    this.turmas$.clear();
  }

  private cached<T>(
    store: Map<string, Observable<T>>,
    key: string,
    factory: () => Observable<T>,
  ): Observable<T> {
    const existing = store.get(key);
    if (existing) {
      return existing;
    }

    const observable = factory().pipe(
      tap({
        error: () => store.delete(key),
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    store.set(key, observable);
    return observable;
  }
}
